import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  Input,
  OnChanges,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { IconComponent } from '@shared/components/icon/icon.component';
import { Icon } from '@shared/components/icon/icon.enum';
import { ValidatorType } from '@shared/models/enums/validator-type.enum';
import { MultiSelect } from '@shared/models/question-types/multi-select';
import { StripHtmlPipe } from '../../../../../shared/pipes/strip-html.pipe';
import { ToValidIdPipe } from '../../../../../shared/pipes/to-valid-id.pipe';
import { ValidatorComponent } from '../../validator/validator.component';
import { QuestionBaseComponent } from '../question-base/question-base.component';
import { MultiSelectInputComponent } from './multi-select-input/multi-select-input.component';

@Component({
  selector: 'app-multi-select',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    ValidatorComponent,
    IconComponent,
    MultiSelectInputComponent,
    QuestionBaseComponent,
    StripHtmlPipe,
    ToValidIdPipe,
  ],
  templateUrl: './multi-select.component.html',
  styleUrl: './multi-select.component.scss',
})
export class MultiSelectComponent implements OnChanges, OnInit {
  private readonly destroyRef = inject(DestroyRef);
  @ViewChild('otherTextarea') otherTextarea?: ElementRef<HTMLTextAreaElement>;
  @ViewChild('statusMessageRef') statusMessageRef?: ElementRef<HTMLElement>;
  @ViewChild(MultiSelectInputComponent)
  multiSelectInputComponent?: MultiSelectInputComponent;
  @Input({ required: true }) question!: MultiSelect;
  @Input({ required: true }) formGroup!: any;
  @Input() otherControl?: any;
  @Input() isOtherSelected: boolean = false;
  @Input() inTable: boolean = false;
  @Input() inMobileAndMatrix: boolean = false;
  @Input() columnTitle: string = '';
  @Input() isFirstQuestion: boolean = false;
  @Input() rowTitle?: string;
  @Input() colGroupTitle?: string;
  @Input() columnIdSuffix?: string;
  @Input() secondColumnStart: boolean = false;
  @Input() parentTableSelector: string = 'app-multi-matrix-table';
  ValidatorType = ValidatorType;
  Icon = Icon;
  statusMessage: string = '';
  disabledCheckboxes: { [key: string]: boolean } = {};

  constructor(
    private cdr: ChangeDetectorRef,
    private translate: TranslateService,
    private elementRef: ElementRef,
  ) {}

  ngOnInit(): void {
    if (!this.formGroup) {
      return;
    }

    const other = this.question.alternatives.find((i) => i.type === 'OTHER');
    if (other) {
      this.isOtherSelected = this.formGroup.get(other.id)?.value;
    }
  }

  ngOnChanges(): void {
    if (!this.formGroup) {
      return;
    }

    this.formGroup.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.toggleCheckboxes();
        this.cdr.detectChanges();
      });
  }

  hasFormGroupBeenTouched(): boolean {
    return Object.keys(this.formGroup.controls).every(
      (key) => this.formGroup.controls[key].touched,
    );
  }

  onChange = (id: string, type: string) => {
    const selectedCount = Object.values(this.formGroup.value).filter(
      (value) => value === true,
    ).length;
    if (
      this.question.maxSelections &&
      selectedCount > this.question.maxSelections
    ) {
      this.formGroup.get(id)?.setValue(false, { emitEvent: false });
      this.toggleCheckboxes();
      this.cdr.detectChanges();
    }

    if (type === 'OTHER') {
      this.isOtherSelected = this.formGroup.get(id)?.value === true;

      if (this.isOtherSelected) {
        setTimeout(() => {
          const textarea = document.getElementById(
            this.question.id + '-other-input',
          ) as HTMLTextAreaElement;
          if (textarea) {
            textarea.focus();
            if (this.inTable) {
              this.recalculateTableOverflow();

              setTimeout(() => {
                const cell = textarea.closest('td');
                if (cell) {
                  cell.scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest',
                    inline: 'end',
                  });
                }
              }, 500);
            }
          }
        }, 0);
      } else {
        // When OTHER is deselected in table, recalculate overflow
        if (this.inTable) {
          setTimeout(() => {
            this.recalculateTableOverflow();
          }, 200);
        }
      }
    }

    if (this.isOtherSelected) {
      this.otherControl?.enable();
    } else {
      this.otherControl?.disable();
    }
  };

  onFocusOut(event: FocusEvent) {
    const relatedTarget = event.relatedTarget as HTMLElement;
    const fieldsetElement = event.currentTarget as HTMLElement;
    if (!fieldsetElement.contains(relatedTarget)) {
      this.formGroup.markAllAsTouched();
    }
  }

  toggleCheckboxes() {
    const selectedCount = Object.values(this.formGroup.value).filter(
      (value) => value === true,
    ).length;
    if (
      this.question.maxSelections &&
      selectedCount >= this.question.maxSelections
    ) {
      Object.keys(this.formGroup.controls).forEach((key) => {
        const control = this.formGroup.get(key);
        this.disabledCheckboxes[key] = !control?.value;
      });
    } else {
      this.statusMessage = '';
      Object.keys(this.formGroup.controls).forEach((key) => {
        this.disabledCheckboxes[key] = false;
      });
    }
  }

  onDisabledCheckboxClick() {
    const selectedCount = Object.values(this.formGroup.value).filter(
      (value) => value === true,
    ).length;
    if (
      this.question.maxSelections &&
      selectedCount >= this.question.maxSelections
    ) {
      this.statusMessage = this.translate.instant(
        'question.multiselect.maximumselected',
        {
          maxSelections: this.question.maxSelections,
        },
      );
      this.cdr.detectChanges();

      // Scroll the status message into view for visibility
      setTimeout(() => {
        if (this.inTable) {
          // For table view, scroll the status message in the main component into view
          if (this.statusMessageRef?.nativeElement) {
            this.statusMessageRef.nativeElement.focus();
            this.statusMessageRef.nativeElement.scrollIntoView({
              behavior: 'smooth',
              block: 'nearest',
            });
          }
        } else {
          // For non-table view, scroll the status message in the child component into view
          this.multiSelectInputComponent?.scrollStatusMessageIntoView();
        }
      }, 0);
    }
  }

  get quotaErrorAlternativeId(): string | undefined {
    return this.formGroup?.errors?.['QuotaError']?.alternativeId;
  }

  getQuotaErrorsForAlternatives(): Set<string> {
    const errorAlternativeIds = new Set<string>();

    if (this.formGroup && this.formGroup.controls) {
      Object.keys(this.formGroup.controls).forEach((alternativeId) => {
        const control = this.formGroup.get(alternativeId);
        if (control?.errors?.['QuotaError']) {
          errorAlternativeIds.add(alternativeId);
        }
      });
    }

    return errorAlternativeIds;
  }

  private recalculateTableOverflow(): void {
    if (this.inTable && this.parentTableSelector) {
      const parentComponent = this.elementRef.nativeElement.closest(
        this.parentTableSelector,
      );

      if (parentComponent) {
        parentComponent.dispatchEvent(new CustomEvent('recalculate-overflow'));
      }
    }
  }
}
